import { worldCenterX, svgLocalMatrix } from "./worldCoords.js";
import { secs } from "../cues/beats.js";
import { evalMaybeRandom } from "../cues/audio/patterns.js";
import { transport } from "./transportState.js";
// ============================================================
//  cuePin.js -- hold elements in view for a while
// ------------------------------------------------------------
//  An element scrolls normally until its centre reaches the
//  playhead. Then it is held: at the playhead (default), or it
//  glides to a screen edge (edge:start|end|left|right) so the
//  material still to come stays readable — box notation with an
//  extent arrow sits at the start edge while the arrow scrolls
//  under the playhead. It lets go after dur beats (wall clock),
//  or, with no duration, when an element carrying pin(release:<uid>)
//  reaches the playhead (put that on the arrow head), or on rewind.
//  A released element scrolls off with the score and only snaps back
//  to its authored position once it is offscreen.
//
//  Coordinate space: SVG world units (same as window.playheadX),
//  through the root svg's CTM for the edges so zoom and the stage
//  translate are included. Horizontal scrolling only — the
//  compensating translate is X-only.
// ============================================================

const SVG_NS = "http://www.w3.org/2000/svg";
const EDGE_MARGIN_PX = 24;       // gap between a held element and the viewport edge
const DEFAULT_GLIDE = 0.5;       // beats: playhead → edge travel when edge: is set

/** element → { wrapper, centerX, halfW, engageX, duration, edge, glide, uid, phase, heldMs, lastOffset, parentScaleX, svg } */
const pins = new Map();

// ============================================================
//  pin(viewport:<anchor>) — held against the SCREEN
// ------------------------------------------------------------
//  The other pins hold an element against the playhead: it stops
//  scrolling, but it still zooms and pans with everything else.
//  This one takes it out of the score's frame entirely. A title
//  page, a watermark, a panel of controls: it sits at the same
//  place and the same size on screen whatever the zoom, the pan
//  and the playhead are doing.
//
//  It is the same trick as the rest of this file — a compensating
//  transform on a wrapper <g> — but the compensation is a whole
//  matrix rather than an x offset, because it has to cancel the
//  zoom as well as the scroll.
//
//  Each frame:
//
//    P = the wrapper's PARENT screen CTM     (score space -> screen)
//    B = the wrapper's own bbox, measured once (it cannot change)
//    R = where on screen it should be        (anchor, size, margin)
//
//  and the wrapper's transform is  T = P-inverse * S,  where S maps
//  B onto R in screen pixels. Composing the two is what keeps the
//  viewBox scale out of it — the rule worldCoords.js exists for.
//
//  The element STAYS in the score's DOM: nothing is moved to an
//  overlay. So the cue walk still finds it, the editor still edits
//  it, its own cues still register, it draws at full vector
//  resolution at any zoom, and its depth is wherever the author put
//  the layer — a watermark under the score, a panel over it.
// ============================================================
const screenPins = new Map();
const DEFAULT_SCREEN_MARGIN = 24;
// performance.now() of the last updatePins frame, 0 when not playing —
// hold/return timing accumulates wall-clock deltas, not window.elapsedTime.
let _lastTick = 0;
/** element → { centerX, targetUid, fired } */
const releases = new Map();
let _autoUid = 1;   // for pins that need a uid only so until: can address them

function pinUidFor(element, opts) {
  if (opts?.uid) return String(opts.uid);
  const id = element.getAttribute?.("id") || "";
  const m = id.match(/uid\s*:\s*([A-Za-z0-9_-]+)/);
  if (m) return m[1];
  return element.dataset?.animUid || (id.includes("(") ? null : id) || null;
}

/** Where the score is actually visible, in screen pixels. */
function viewportRect() {
  const host = document.getElementById("scoreContainer")
    || document.getElementById("scoreInner")
    || document.documentElement;
  const r = host?.getBoundingClientRect?.();
  if (r && r.width > 0 && r.height > 0) return r;
  return { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight };
}

const SCREEN_ANCHORS = new Set([
  "center", "centre", "top", "bottom", "left", "right",
  "topleft", "topright", "bottomleft", "bottomright",
]);

/**
 * The screen rectangle a viewport-pinned element should occupy.
 * size: fit (contain, the default) - cover - actual (one user unit per pixel)
 * - or a number, read as a percentage of the viewport's width.
 */
export function placeRect(B, vp, state) {
  const m = state.margin ?? DEFAULT_SCREEN_MARGIN;
  const availW = Math.max(1, vp.width - 2 * m);
  const availH = Math.max(1, vp.height - 2 * m);

  let scale;
  if (state.size === "cover") scale = Math.max(availW / B.width, availH / B.height);
  else if (state.size === "actual") scale = 1;
  else if (typeof state.size === "number") scale = (vp.width * state.size / 100) / B.width;
  else scale = Math.min(availW / B.width, availH / B.height);

  const w = B.width * scale, h = B.height * scale;
  const a = String(state.anchor || "center");
  const x = a.includes("left")  ? vp.x + m
          : a.includes("right") ? vp.x + vp.width - m - w
          : vp.x + (vp.width - w) / 2;
  const y = a.includes("top")    ? vp.y + m
          : a.includes("bottom") ? vp.y + vp.height - m - h
          : vp.y + (vp.height - h) / 2;
  return { x, y, width: w, height: h };
}

// 2D affine matrices as {a,b,c,d,e,f}, the same six numbers an SVG transform
// takes: x' = a*x + c*y + e, y' = b*x + d*y + f.
//
// Done with plain numbers rather than DOMMatrix on purpose. getScreenCTM may
// hand back an SVGMatrix or a DOMMatrix depending on the browser, and calling
// one's multiply() with the other throws — which, inside a try/catch in a
// frame loop, is a pin that silently never moves. Arithmetic cannot mismatch,
// and it can be tested without a document.

/** M then N: the matrix that applies N first, then M. */
export function composeMatrix(M, N) {
  return {
    a: M.a * N.a + M.c * N.b,
    b: M.b * N.a + M.d * N.b,
    c: M.a * N.c + M.c * N.d,
    d: M.b * N.c + M.d * N.d,
    e: M.a * N.e + M.c * N.f + M.e,
    f: M.b * N.e + M.d * N.f + M.f,
  };
}

/** The inverse, or null where there is none (a collapsed matrix). */
export function invertMatrix(M) {
  const det = M.a * M.d - M.b * M.c;
  if (!det || !Number.isFinite(det)) return null;
  return {
    a:  M.d / det,
    b: -M.b / det,
    c: -M.c / det,
    d:  M.a / det,
    e: (M.c * M.f - M.d * M.e) / det,
    f: (M.b * M.e - M.a * M.f) / det,
  };
}

/** The matrix that puts box B onto rectangle R. */
export function fitMatrix(B, R) {
  const sx = R.width / B.width;
  const sy = R.height / B.height;
  return { a: sx, b: 0, c: 0, d: sy, e: R.x - sx * B.x, f: R.y - sy * B.y };
}

/** size: as written -> what placeRect expects. */
export function readPinSize(raw) {
  const v = raw == null ? "fit" : String(raw).toLowerCase().replace("%", "");
  if (["fit", "cover", "actual"].includes(v)) return v;
  return Number.isFinite(Number(v)) ? Number(v) : "fit";
}

function registerScreenPin(element, opts) {
  if (screenPins.has(element)) return;
  const asked = String(opts.viewport || "center").toLowerCase();
  const known = SCREEN_ANCHORS.has(asked);
  if (!known) {
    console.warn(`[cuePin] viewport:${asked} is not an anchor — using center. One of: ${[...SCREEN_ANCHORS].join(", ")}`);
  }

  const wrapper = document.createElementNS(SVG_NS, "g");
  wrapper.classList.add("pin-wrapper", "pin-viewport");
  element.parentNode.insertBefore(wrapper, element);
  wrapper.appendChild(element);
  // On screen by definition, so the offscreen observer must leave whatever is
  // animating inside it alone.
  wrapper.setAttribute("data-pin-held", "1");

  if (opts.opacity != null && Number.isFinite(Number(opts.opacity))) {
    wrapper.style.opacity = String(Math.min(1, Math.max(0, Number(opts.opacity))));
  }

  screenPins.set(element, {
    wrapper,
    anchor: known ? asked : "center",
    size: readPinSize(opts.size),
    margin: Number.isFinite(Number(opts.margin)) ? Number(opts.margin) : DEFAULT_SCREEN_MARGIN,
    // Measured lazily: at registration the SVG may not be laid out, and a zero
    // box would scale the element away to nothing.
    natural: null,
    lastMatrix: "",
  });
  startScreenPinLoop();
}

// The transport calls updatePins() while the score is scrolling, which is not
// when a viewport pin needs the most attention: a zoom, a pinch, a pan, a
// window resize or a fullscreen toggle all move the screen under an element
// that is standing still. So these keep their own loop while any exist. It
// costs one matrix read per pinned layer per frame and writes nothing unless
// the answer changed, which on a still score is never.
let _screenRaf = 0;
function screenPinLoop() {
  _screenRaf = 0;
  updateScreenPins();
  if (screenPins.size) _screenRaf = requestAnimationFrame(screenPinLoop);
}
function startScreenPinLoop() {
  if (!_screenRaf && screenPins.size) _screenRaf = requestAnimationFrame(screenPinLoop);
}

/** Hold every viewport-pinned element where it belongs on screen. */
function updateScreenPins() {
  if (!screenPins.size) return;
  const vp = viewportRect();

  for (const [element, state] of screenPins) {
    if (!element.isConnected) { screenPins.delete(element); continue; }

    // The wrapper's own bbox, in the space its transform maps FROM. Taken with
    // the transform off, so a previous frame's placement is not baked into it.
    if (!state.natural || !(state.natural.width > 0)) {
      const held = state.wrapper.getAttribute("transform");
      state.wrapper.removeAttribute("transform");
      let box = null;
      try { box = state.wrapper.getBBox(); } catch { /* not rendered yet */ }
      if (held) state.wrapper.setAttribute("transform", held);
      if (!box || !(box.width > 0 && box.height > 0)) continue;   // measure again next frame
      state.natural = { x: box.x, y: box.y, width: box.width, height: box.height };
    }

    let P = null;
    try { P = state.wrapper.parentNode?.getScreenCTM?.() ?? null; } catch { /* detached */ }
    if (!P) continue;

    const B = state.natural;
    const R = placeRect(B, vp, state);
    const Pinv = invertMatrix({ a: P.a, b: P.b, c: P.c, d: P.d, e: P.e, f: P.f });
    if (!Pinv) continue;                       // the score is collapsed to nothing
    const T = composeMatrix(Pinv, fitMatrix(B, R));
    if (!Number.isFinite(T.a) || !Number.isFinite(T.e)) continue;

    const m = `matrix(${T.a} ${T.b} ${T.c} ${T.d} ${T.e} ${T.f})`;
    if (m !== state.lastMatrix) {
      state.wrapper.setAttribute("transform", m);
      state.lastMatrix = m;
    }
    placeOverlaysIn(state);
  }
}

// The HTML overlays that make elements clickable (button.js) sit in the
// scrolling score layer, placed from the element's screen box. Held against
// the screen, the element moves against the scroll — every frame the score
// scrolls, is dragged, or a camera hold pans — so its overlays are put back
// over it here, in the same frame, or they lag (wiggle) and then part from it.
const OVERLAY_SELECTOR = ".oscilla-click-overlay, .oscilla-drag-click-overlay, .oscilla-button-overlay";
function placeOverlaysIn(state) {
  const now = performance.now();
  // which overlays belong to this pin: looked up again now and then, as
  // overlays are built after the pin and rebuilt on a reload
  if (!state.overlays || now - (state.overlaysAt || 0) > 2000) {
    state.overlays = [...document.querySelectorAll(OVERLAY_SELECTOR)]
      .filter(o => o._svgElement && typeof o._place === "function" && state.wrapper.contains(o._svgElement));
    state.overlaysAt = now;
  }
  if (!state.overlays.length) return;
  const box = state.wrapper.getBoundingClientRect();
  if (!(box.width > 0)) return;                 // hidden (a closed panel): nothing to place
  for (const o of state.overlays) {
    if (o.isConnected) o._place();
  }
}

/**
 * Seconds a pin takes from the playhead to its edge: glide: in beats, half a
 * beat when an edge is set without one, none when it stays at the playhead.
 * `k` is seconds a beat (tempo()'s, by default).
 */
export function pinGlideSeconds(glide, edge, k) {
  if (glide != null && Number.isFinite(Number(glide))) return Number(secs(glide, k));
  return edge === "playhead" ? 0 : Number(secs(DEFAULT_GLIDE, k));
}

/**
 * @param {Element} element
 * @param {number|null} durationBeats  beats held (of tempo(), as it is now); 0/null = until released
 * @param {{edge?:string, glide?:number, uid?:string, engage?:string, until?:string}} [opts]
 */
export function registerPin(element, durationBeats, opts = {}) {
  // viewport:<anchor> is a different kind of hold — against the screen rather
  // than the playhead — and shares none of the state machine below.
  if (opts?.viewport) return registerScreenPin(element, opts);
  if (!element) return;
  if (pins.has(element)) return;

  // rand() / choose() roll once, as the pin is registered
  durationBeats = evalMaybeRandom(durationBeats);
  opts = { ...opts, glide: evalMaybeRandom(opts.glide), fade: evalMaybeRandom(opts.fade) };
  const dur = Number(secs(durationBeats));      // beats -> seconds
  // ms of WALL time. window.elapsedTime is score time — inside a speed(3)
  // region it runs three times faster than the clock on the wall, and a pin
  // timed against it would hold for under 3 real seconds when asked for 8.
  // The held time is accumulated frame by frame in updatePins(), so it
  // only counts while playing: a pause freezes the hold rather than eating it.
  const duration = Number.isFinite(dur) && dur > 0 ? dur * 1000 : Infinity;
  const edge = String(opts.edge || "playhead").toLowerCase();
  // engage:playhead (default) holds the moment the centre meets the playhead,
  // then glides to the edge. engage:edge leaves the element alone until it has
  // scrolled all the way to that edge, and holds it there — no leap across the
  // screen, and no glide to animate, since it is already where it belongs.
  // engage:trailing waits for the element's TRAILING (right) edge to meet the
  // playhead instead of its centre — on a wide group of trig:playhead members
  // a centre-engaged pin freezes the right half before the playhead ever
  // crosses it, so those members never fire. Trailing engagement lets the
  // playhead sweep the whole group first, then holds it riding just behind
  // the playhead.
  const engage = String(opts.engage || "playhead").toLowerCase();
  // engage:now -- pin(now) -- held where it is from this moment, and through
  // any rewind or seek: what the console draws (spot > pin(now)) belongs to
  // the stage the performer sees, not to a place in the score. Held against
  // where the playhead is now, so it keeps its place on the screen.
  const now = engage === "now";
  // engage:wrap -- pin(wrap) -- scrolls with the score, but round the screen:
  // gone off one edge, it comes in at the other, a conveyor that never runs
  // out (three boxes a third of the screen apart: always the screen full)
  const wrap = engage === "wrap";
  const holdOnArrival = engage === "edge" && edge !== "playhead";
  const glideS = holdOnArrival ? 0 : pinGlideSeconds(opts.glide, edge);

  const { centerX, halfW } = worldCenterX(element);
  // The wrapper's translate is in the units of the element's PARENT (it is
  // inserted there), so the scale that converts it is the parent's, not the
  // element's own: an element carrying its own scale(1.1) was held with a
  // 10% shortfall and crept back against the playhead.
  const pm = element.parentNode instanceof SVGGraphicsElement ? svgLocalMatrix(element.parentNode) : null;
  const parentScaleX = pm?.a || 1;
  const engageX = now ? (Number(transport.playheadX) || 0) : engage === "trailing" ? centerX + halfW : centerX;
  const wrapper = document.createElementNS(SVG_NS, "g");
  wrapper.classList.add("pin-wrapper");
  // live: cues inside are measured where the hold puts them (cueDispatcher.js)
  if (opts.live) wrapper.setAttribute("data-pin-live", "1");
  element.parentNode.insertBefore(wrapper, element);
  wrapper.appendChild(element);

  // until:<id> needs a uid for the release to address, even when none was
  // authored — the pin releases itself, so the name never has to be shared.
  let uid = pinUidFor(element, opts);
  if (!uid && opts.until) uid = `pin-${(_autoUid++).toString(36)}`;

  pins.set(element, {
    wrapper, centerX, halfW, engageX, parentScaleX,
    duration, edge, glide: glideS * 1000, holdOnArrival,
    uid,
    svg: element.ownerSVGElement,
    phase: "waiting", heldMs: 0, lastOffset: 0,
    always: now || wrap,
    wrap,
    element,
    // let go (dur:, until:, a release point): it scrolls off with the score
    // (as ever) -- or with fade:8 fades out over 8 beats where it is held,
    // with then:remove at once, and is taken away (goAway)
    then: String(opts.then) === "remove" ? "remove" : opts.fade != null ? "fade" : "drift",
    fade: opts.fade,
  });
  adoptDrawing(pins.get(element));
  if (now || wrap) engageNow(pins.get(element), engageX);

  // until:<id> — the pin names its own release object: when the element with
  // that SVG id (or cue uid) reaches the playhead, this pin lets go. The
  // one-object form of pin(release:<uid>)-on-the-releaser; both keep working.
  if (opts.until) {
    const sel = String(opts.until);
    const svg = element.ownerSVGElement;
    const relEl = document.getElementById(sel)
      || document.getElementById(`rehearsal(${sel})`)          // until:B -- rehearsal mark B
      || svg?.querySelector(`[data-anim-uid="${CSS.escape(sel)}"]`)
      || svg?.querySelector(`[data-oscilla*="uid:${CSS.escape(sel)}"]`);
    if (relEl) registerPinRelease(relEl, uid);
    else console.warn(`[cuePin] until:${sel} — no element with that id or uid; pin will only release on rewind${duration === Infinity ? "" : " or dur"}`);
  }
}

/** This element lets go of the held element with `targetUid` when its centre reaches the playhead. */
export function registerPinRelease(element, targetUid) {
  if (!element || !targetUid) return;
  // one release point for several pins (spot1 and spot2 until:s2open): each let go
  const had = releases.get(element);
  if (had) { had.targets.add(String(targetUid)); return; }
  const { centerX } = worldCenterX(element);
  releases.set(element, { centerX, targets: new Set([String(targetUid)]), fired: false });
}

/** Release every held pin with this uid now. Returns how many. */
export function releasePin(uid) {
  let n = 0, cancelled = 0;
  for (const state of pins.values()) {
    if (state.uid !== String(uid)) continue;

    if (state.phase === "pinned") {
      if (state.then === "drift") startReturn(state);
      else goAway(state);
      n++;
    } else if (state.phase === "waiting") {
      // The release point went past before this element ever came to rest.
      // With engage:edge that happens whenever the element is still short of
      // the edge — it needs the distance from the edge to the playhead to get
      // there, so one sitting too close to its release point never holds.
      // Its window is over either way: let it scroll on, and make sure it does
      // not grab the edge later with nothing left to release it.
      state.phase = "released";
      state.lastOffset = 0;
      state.wrapper.removeAttribute("transform");
      setHeld(state, false);
      cancelled++;
    }
  }
  if (cancelled) console.warn(`[cuePin] uid=${uid}: ${cancelled} not held yet at the release point - skipped the hold. Move it earlier, or the release later, to give it room.`);
  return n + cancelled;
}

/**
 * Mark or unmark the wrapper as actively holding.
 *
 * oscillaObserver pauses an animation whose element leaves the viewport. A
 * held element sits hard against the screen edge, so its animated children
 * cross that boundary as they scale and rotate, and each crossing paused or
 * resumed that one child — the glitching seen along the left edge. The
 * observer skips anything inside [data-pin-held], so held material keeps
 * animating; the flag is only present while the pin is in its held phase, so
 * offscreen pausing still applies before and after.
 */
function setHeld(state, held) {
  if (!state?.wrapper) return;
  if (held) state.wrapper.setAttribute("data-pin-held", "1");
  else state.wrapper.removeAttribute("data-pin-held");
}

// world x under a screen x, via the root svg (stage translate + zoom included)
function worldXAtScreen(svg, screenX) {
  const m = svg?.getScreenCTM?.();
  if (!m) return null;
  const pt = svg.createSVGPoint();
  pt.x = screenX; pt.y = 0;
  return pt.matrixTransform(m.inverse()).x;
}

function anchorFor(state, playX) {
  let edge = state.edge;
  if (edge === "start") edge = transport.scrollReversed ? "right" : "left";
  else if (edge === "end") edge = transport.scrollReversed ? "left" : "right";
  if (edge === "left") {
    const w = worldXAtScreen(state.svg, EDGE_MARGIN_PX);
    return w == null ? playX : w + state.halfW;
  }
  if (edge === "right") {
    const w = worldXAtScreen(state.svg, window.innerWidth - EDGE_MARGIN_PX);
    return w == null ? playX : w - state.halfW;
  }
  return playX;
}

/**
 * Let go with fade: or then:remove (pin(now, until:s2open, fade:8)): held
 * where it is while it fades over fade: beats, then taken away -- a shape
 * the console made removed as
 * shape(remove:1) removes it (its hold goes with it), anything else left
 * unseen until a rewind brings it back.
 */
function goAway(state) {
  state.phase = "going";
  const sec = state.then === "fade" ? Math.max(0, Number(secs(state.fade)) || 0) : 0;
  const w = state.wrapper;
  w.style.transition = sec ? `opacity ${sec}s linear` : "";
  w.style.opacity = "0";
  clearTimeout(state.goneTimer);
  state.goneTimer = setTimeout(() => {
    if (state.phase !== "going") return;
    state.phase = "gone";
    setHeld(state, false);
    const el = state.element;
    if (el?.classList?.contains("oscilla-shape")) import("../cues/shape.js").then(m => m.removeShape(el));
  }, sec * 1000);
}

/** A pin that went away (goAway) back in sight, waiting to be held again: a rewind. */
function comeBack(state) {
  clearTimeout(state.goneTimer);
  state.wrapper.style.transition = "";
  state.wrapper.style.opacity = "";
  state.wrapper.removeAttribute("transform");
  state.phase = "waiting";
  state.heldMs = 0;
  state.lastOffset = 0;
  setHeld(state, false);
}

/**
 * Let go: freeze the offset and let the element scroll off with the score.
 *
 * It does not glide back to its authored position: that would move released
 * material BACKWARDS across whatever is coming up. The element resumes
 * scrolling from where it was held, and once it has fully left the screen
 * the offset is dropped:
 * the snap back to the authored position happens where nobody can see it,
 * so a rewound pass still finds the element exactly where it was drawn.
 */
function startReturn(state) {
  state.phase = "drifting";
  setHeld(state, false);
  if (!state.lastOffset) {                 // never displaced: done already
    state.wrapper.removeAttribute("transform");
    state.phase = "released";
  }
}

function engage(state, playX) {
  state.phase = "pinned";
  state.heldMs = 0;
  state.holdShift = 0;
  // Held material must stay animated even where it overlaps the
  // viewport edge: see setHeld().
  setHeld(state, true);
  // Hold on this very frame. Leaving it to the next one lets the
  // element travel past the edge unheld and then jump back.
  applyHold(state, playX);
}

/**
 * A sound's box (viz(s3), an adc input's -- chair > pin(now)) is a rect, and
 * what it shows is drawn in a group of its own beside it (scope.js): held
 * with the box, in the hold's wrapper. Looked for each frame, as a drawing is
 * made again (another view:, the input opened again) beside the box.
 */
function adoptDrawing(state) {
  const el = state?.element;
  if (el?.dataset?.liveAudio == null || !el.id) return;
  for (const g of state.svg?.querySelectorAll(`g.oscilla-scope[data-uid="${CSS.escape(el.id)}"]`) || []) {
    if (g.parentNode !== state.wrapper) state.wrapper.appendChild(g);
  }
}

/** engage() under another name, for registerPin, where `engage` is the option. */
const engageNow = (state, playX) => engage(state, playX);

/**
 * Take hold of every waiting pin the playhead is already past. Engaging needs
 * the transport running (updatePins), so dragging past a pin grabs nothing;
 * this is for a deliberate placement with the transport stopped — a map-mode
 * route starting at a station (route.js), which is the piece as if played up
 * to there. Returns how many pins took hold.
 */
export function engageReached() {
  const playX = transport.playheadX;
  if (playX == null) return 0;
  let n = 0;
  for (const [, state] of pins) {
    if (state.phase === "waiting" && hasReached(state, playX)) { engage(state, playX); n++; }
  }
  return n;
}

/**
 * Has the playhead reached the point where this pin should be holding?
 *
 * Two different tests, and they must be the same test everywhere or the pin
 * flickers: engage:edge holds as soon as the element's leading edge meets the
 * screen edge, which for a wide element happens while the playhead is still
 * short of its centre, so comparing playX against centerX would judge an
 * edge-engaged pin "rewound" on the very next frame.
 */
function hasReached(state, playX) {
  if (state.always) return true;             // pin(now): never rewound past
  return state.holdOnArrival
    ? anchorFor(state, playX) >= state.engageX
    : playX >= state.engageX;
}

/**
 * Put the element at its anchor for this frame and remember the offset.
 * Shared by the frame the pin engages on and every frame after, so the
 * element never renders unheld for a frame between the two.
 *
 * @returns {{offset:number, pinElapsed:number}}
 */
function applyHold(state, playX) {
  const pinElapsed = state.heldMs;
  if (state.wrap) return applyWrap(state, pinElapsed);
  let anchor = anchorFor(state, playX);
  if (state.glide > 0 && anchor !== playX) {
    const t = Math.min(1, pinElapsed / state.glide);
    const ease = t * t * (3 - 2 * t);    // smoothstep
    anchor = playX + (anchor - playX) * ease;
  }
  // Parent-local units: the wrapper sits inside the element's parent.
  // Playhead pins hold at the ENGAGEMENT point, so engage:trailing rides with
  // the trailing edge on the playhead and there is no leap at the moment of
  // engagement (offset starts at 0 either way). Screen-edge pins keep the
  // centre reference so the element parks fully visible at the edge.
  const holdRef = state.edge === "playhead" ? state.engageX : state.centerX;
  // holdShift (world units, shiftPinHold): the element held that much further
  // along, relative to the playhead — e.g. a zoom() bringing a constellation
  // of a pinned sky to just right of the playhead
  const offset = (anchor - holdRef + (state.holdShift || 0)) / state.parentScaleX;
  state.lastOffset = offset;
  state.wrapper.setAttribute("transform", `translate(${offset}, 0)`);
  return { offset, pinElapsed };
}

/**
 * pin(wrap): where the score has scrolled the element to, moved round a loop
 * one screen and one element wide: gone wholly off the left edge, it comes
 * back wholly off the right one and slides in -- no jump in sight. Boxes
 * spaced evenly round the loop -- (screen + box) / n apart -- make a conveyor
 * with no seam. Measured where it is without this hold, each frame, so a box
 * run again with another at:, or dragged, wraps from where it is put.
 */
function applyWrap(state, pinElapsed) {
  const L = worldXAtScreen(state.svg, 0), R = worldXAtScreen(state.svg, window.innerWidth);
  if (L == null || R == null || !(R > L)) return { offset: state.lastOffset || 0, pinElapsed };
  let cx = state.centerX, halfW = state.halfW;
  try {
    const m = worldCenterX(state.element);
    cx = m.centerX - (state.lastOffset || 0) * state.parentScaleX;
    halfW = m.halfW;
  } catch { /* not drawn this frame: as it was */ }
  const P = R - L + 2 * halfW;                       // the loop: the screen, and the element's own width
  const k = Math.floor((cx - L + halfW) / P);       // its left edge kept in [-width, screen)
  const offset = (-k * P) / state.parentScaleX;
  state.lastOffset = offset;
  state.wrapper.setAttribute("transform", `translate(${offset}, 0)`);
  return { offset, pinElapsed };
}

export function updatePins() {
  // Viewport pins first, and outside both guards below: they answer to the
  // screen, not the playhead, so they must keep their place on a score that
  // has never played and has no playhead position at all.
  updateScreenPins();

  if (pins.size === 0 && releases.size === 0) return;

  const playX = transport.playheadX;
  if (playX == null) return;

  // The state machine runs whether or not the transport is moving, so that
  // scrubbing past a pin's boundaries still engages nothing and, crucially,
  // still LETS GO: gated on isPlaying, a drag would leave the wrapper's
  // offset frozen and the held element would travel with the score instead
  // of going back where it was drawn.
  //
  // Only two things need the transport to be running: held time, which must
  // not drain while paused, and engagement, which must not grab an element
  // the user is merely dragging past.
  const playing = transport.isPlaying === true && !window.isSeeking;
  // Wall-clock frame delta for the hold. Capped so the first frame after a
  // pause or a hidden-tab stall does not swallow the whole hold.
  const wall = performance.now();
  const dt = (playing && _lastTick) ? Math.min(wall - _lastTick, 100) : 0;
  _lastTick = playing ? wall : 0;

  for (const [element, state] of pins) {
    // taken away (a scene going, shape(remove:1)): its hold goes with it
    if (!element.isConnected) {
      if (!state.wrapper.childNodes.length) state.wrapper.remove();
      pins.delete(element);
      continue;
    }
    adoptDrawing(state);
    switch (state.phase) {
      case "waiting": {
        // Normally the element is caught as it meets the playhead. With
        // holdOnArrival it keeps scrolling until the edge anchor — a world
        // position that advances with the stage — catches up to its centre,
        // so it comes to rest exactly where it already is. pin(now) takes
        // hold again at once after a rewind, playing or not.
        if ((playing || state.always) && hasReached(state, playX)) engage(state, playX);
        break;
      }
      case "pinned": {
        if (!hasReached(state, playX)) {       // genuinely rewound: back to waiting
          state.wrapper.removeAttribute("transform");
          state.phase = "waiting";
          state.heldMs = 0;
          setHeld(state, false);
          break;
        }
        state.heldMs += dt;
        const { pinElapsed } = applyHold(state, playX);
        if (pinElapsed >= state.duration) {
          if (state.then === "drift") startReturn(state);
          else goAway(state);
        }
        break;
      }
      case "sweeping": {
        // Let go for a sweep (sweepPin): the offset is frozen, so the element
        // scrolls with the score and the playhead passes through it; once
        // sweepUntilX reaches the playhead it is taken hold of again, right
        // where it is.
        if (!hasReached(state, playX)) {
          state.wrapper.removeAttribute("transform");
          state.phase = "waiting";
          state.lastOffset = 0;
          state.holdShift = 0;
          setHeld(state, false);
          break;
        }
        if (playing && playX >= state.sweepUntilX) {
          reengage(state, playX);
        }
        break;
      }
      case "drifting": {
        if (!hasReached(state, playX)) {     // rewound mid-drift: start over
          state.wrapper.removeAttribute("transform");
          state.phase = "waiting";
          state.lastOffset = 0;
          break;
        }
        // Offset frozen — the element scrolls with the score. Once it has
        // fully left the screen, drop the offset: the snap home is invisible.
        const r = state.wrapper.getBoundingClientRect?.();
        if (r && (r.right < 0 || r.left > window.innerWidth)) {
          state.wrapper.removeAttribute("transform");
          state.lastOffset = 0;
          state.phase = "released";
        }
        break;
      }
      case "going":
      case "gone": {
        // held where it is while it fades (fade:); rewound past a
        // playhead pin's hold, it is back, to be held again on the next pass
        if (!hasReached(state, playX)) { comeBack(state); break; }
        if (state.phase === "going") applyHold(state, playX);
        break;
      }
      case "released": {
        if (!hasReached(state, playX)) {
          state.wrapper.removeAttribute("transform");
          state.phase = "waiting";
          setHeld(state, false);
        }
        break;
      }
    }
  }

  for (const [element, rel] of releases) {
    // gone with its score (a project switch, a scene): it releases nothing,
    // least of all a pin of the next score that has the same uid
    if (!element.isConnected) { releases.delete(element); continue; }
    if (!rel.fired && playX >= rel.centerX) { rel.fired = true; for (const uid of rel.targets) releasePin(uid); }
    else if (rel.fired && playX < rel.centerX) rel.fired = false;   // rewound: arm again
  }
}

/** Take hold again from where the element is now (no jump): the hold's shift
 *  is set so the offset the element already has is the one it keeps. */
function reengage(state, playX = Number(transport.playheadX) || 0) {
  const holdRef = state.edge === "playhead" ? state.engageX : state.centerX;
  state.holdShift = (state.lastOffset || 0) * state.parentScaleX - (anchorFor(state, playX) - holdRef);
  state.phase = "pinned";
}

/**
 * Let a held element go for a sweep: it stays where it is in the score while
 * the playhead passes through it, and is taken hold of again (where it then is)
 * once the right edge of `untilNode` — plus margin — has reached the playhead.
 */
export function sweepPin(node, untilNode, margin = 0) {
  for (const [, state] of pins) {
    if (state.phase !== "pinned" || !state.wrapper.contains(node)) continue;
    const inv = state.svg?.getScreenCTM?.()?.inverse?.();
    const r = (untilNode || node).getBoundingClientRect();
    if (!inv) return false;
    const right = new DOMPoint(r.right, r.top).matrixTransform(inv).x;
    state.sweepUntilX = right + margin;
    state.phase = "sweeping";
    return true;
  }
  return false;
}

/**
 * Move a held element along relative to the playhead by dx world units (the
 * hold keeps the new relation from then on). Returns false if nothing holds it.
 * A sweeping element is taken hold of again first.
 */
export function shiftPinHold(node, dx) {
  for (const [, state] of pins) {
    if (!state.wrapper.contains(node)) continue;
    if (state.phase === "sweeping") reengage(state);
    if (state.phase !== "pinned") continue;
    state.holdShift = (state.holdShift || 0) + dx;
    state.lastOffset = (state.lastOffset || 0) + dx / state.parentScaleX;
    state.wrapper.setAttribute("transform", `translate(${state.lastOffset}, 0)`);
    return true;
  }
  return false;
}

/** Whether a pin is holding this node (or an ancestor of it) at the playhead right now. */
export function isHeldByPin(node) {
  for (const [, state] of pins) {
    if ((state.phase === "pinned" || state.phase === "sweeping") && state.wrapper.contains(node)) return true;
  }
  return false;
}
if (typeof window !== "undefined") window.oscillaPins = { ...(window.oscillaPins || {}), isHeldByPin, shiftPinHold, sweepPin };

/** Rewind to start: everything back to waiting (wrappers stay for the next pass). */
export function clearAllPins() {
  for (const state of pins.values()) {
    if (state.phase === "going" || state.phase === "gone") comeBack(state);
    setHeld(state, false);
    state.wrapper.removeAttribute("transform");
    state.phase = "waiting";
    state.heldMs = 0;
    state.lastOffset = 0;
  }
  for (const rel of releases.values()) rel.fired = false;
  // A viewport pin has no position in the score to rewind to. Only the
  // measurement is dropped, so a score that changed size is measured again.
  for (const state of screenPins.values()) { state.natural = null; state.lastMatrix = ""; }
}

/** For the console and the tests. */
export function screenPinCount() { return screenPins.size; }
